// IDwsThemeService: writes theme tokens as CSS variables on :root and remembers them for js/dws.theme.js, which
// restores them before the first paint of the next page load.

import { publish } from "../shared/publish";

// The theme's colours as last written to :root, kept in localStorage so that DWSBlazorTheme.js (a small script in the
// host page's <head>) can put them back before the first paint of the next page load. Without it every load paints
// the stylesheet's default palette until the runtime has started and the theme service has run again.
const DWS_THEME_STORAGE_KEY = 'dws.theme.vars';

/** A theme token as IDwsThemeService sends it: the custom property name without the leading dashes, and its value. */
interface DwsStyleProperty {
    name: string;
    value: string | null;
}

function DwsRememberThemeProperties(entries: DwsStyleProperty[]): void {
    try {
        let stored = {};
        let raw = window.localStorage.getItem(DWS_THEME_STORAGE_KEY);

        if (raw) {
            let parsed = JSON.parse(raw);

            if (parsed && typeof parsed === 'object') {
                stored = parsed;
            }
        }

        for (let i = 0; i < entries.length; i++) {
            if (entries[i] && entries[i].name && typeof entries[i].value === 'string') {
                stored[entries[i].name] = entries[i].value;
            }
        }

        window.localStorage.setItem(DWS_THEME_STORAGE_KEY, JSON.stringify(stored));
    } catch (e) {
        // Storage is blocked or full: the theme still applies, only the next load starts in the default palette.
    }
}

function BulkChangeStyleProperties(properties: DwsStyleProperty[]): boolean {
    if (!properties || properties.length === 0) {
        return false;
    }

    let root = document.documentElement;
    let success = true;

    properties.forEach(property => {
        if (property.name && property.value != null) {
            root.style.setProperty('--' + property.name, property.value);

            // Verify the property was set: read back the declared inline value, not the computed one. Tokens may
            // reference other tokens (var(--text), color-mix(...)), and the computed value resolves those, so it
            // never equals the written text. A rejected value (e.g. unbalanced brackets) leaves the declaration
            // empty or unchanged, which still fails here.
            let declaredValue = root.style.getPropertyValue('--' + property.name).trim();
            if (declaredValue !== String(property.value).trim()) {
                success = false;
            }
        } else {
            success = false;
        }
    });

    DwsRememberThemeProperties(properties);

    return success;
}

function ChangeStyleProperty(nameVariable: string, value: string | null): boolean {
    if (value == null)
        return false;

    let root = document.documentElement;
    root.style.setProperty('--' + nameVariable, value);

    // Verify if the property was set correctly
    let computedValue = getComputedStyle(root).getPropertyValue('--' + nameVariable).trim();

    DwsRememberThemeProperties([{ name: nameVariable, value: value }]);

    return computedValue === value;
}

publish("BulkChangeStyleProperties", BulkChangeStyleProperties);
publish("ChangeStyleProperty", ChangeStyleProperty);
